import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    Network,
    Globe,
    Monitor,
    Shield,
    Zap,
    Server,
    ArrowRight,
    Cable,
    Radio,
    Building,
    Phone,
    Video,
    Lock,
    Flame,
    Cloud,
    Settings,
    Sun,
    Battery,
    Users,
    BarChart3,
} from 'lucide-react';

const fadeUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
};

const ServicesPage = () => {
    const coreCompetencies = [
        {
            id: 'network',
            icon: Network,
            title: 'Network Infrastructure Buildout & System Integration',
            description: 'Complete network infrastructure solutions from design to deployment.',
            features: [
                { icon: Cable, text: 'Structured Cabling Systems for LAN Connectivity & Clean Power' },
                { icon: Radio, text: 'Outdoor Infrastructure: Fiber Optics, Microwave Radio Links' },
                { icon: Building, text: 'Comms Room Design, Buildout & Management' },
            ],
        },
        {
            id: 'connectivity',
            icon: Globe,
            title: 'Global Connectivity Solutions',
            description: 'Enterprise-grade unified communications for global operations.',
            features: [
                { icon: Phone, text: 'Unified Communications' },
                { icon: Globe, text: 'Global Telephony Architecture & SIP Trunking' },
            ],
        },
        {
            id: 'av',
            icon: Monitor,
            title: 'Audio-Visual Solutions',
            description: 'State-of-the-art AV systems diverse use cases.',
            features: [
                { icon: Video, text: 'Conference Room AV Systems' },
                { icon: Monitor, text: 'Digital Signage & Display Solutions' },
            ],
        },
        {
            id: 'building',
            icon: Shield,
            title: 'Integrated Building Management Systems',
            description: 'Comprehensive security and building automation solutions.',
            features: [
                { icon: Video, text: 'Video Surveillance Infrastructure' },
                { icon: Lock, text: 'Access Control & Identity Management' },
                { icon: Flame, text: 'Automated Fire Detection, Alarm & Suppression Systems' },
                { icon: Users, text: 'Visitor Management Solutions' },
                { icon: Lock, text: 'Door Interlock Systems for Airlock Applications' },
            ],
        },
        {
            id: 'automation',
            icon: Server,
            title: 'Business Process Automation & Managed Services',
            description: 'Digital transformation through automation and managed IT services.',
            features: [
                { icon: Cloud, text: 'Digital Business Automation (SaaS)' },
                { icon: Settings, text: 'Information Technology Service Management (SaaS)' },
                { icon: Cloud, text: 'Microsoft M365 (SaaS)' },
                { icon: Server, text: 'Veeam Backup Solutions' },
                { icon: BarChart3, text: 'New Relic' },
            ],
        },
        {
            id: 'power',
            icon: Zap,
            title: 'On-Grid & Off-Grid Power Solutions',
            description: 'Reliable power infrastructure for enterprise environments.',
            features: [
                { icon: Battery, text: 'UPS Power Distribution for Enterprise LAN environments' },
                { icon: Zap, text: 'Renewable Energy Infrastructure' },
                { icon: Sun, text: 'Solar Power Deployment' },
            ],
        },
    ];

    return (
        <div data-testid="services-page" className="min-h-screen">
            {/* Hero Section */}
            <section className="relative pt-44 pb-24 bg-slate-900 overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img
                        src="https://images.unsplash.com/photo-1680992046617-e2e35451bcdb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzZXJ2ZXIlMjByb29tJTIwYmx1ZSUyMGxpZ2h0c3xlbnwwfHx8fDE3NzIyMTEzNDZ8MA&ixlib=rb-4.1.0&q=85"
                        alt="Server infrastructure"
                        className="w-full h-full object-cover opacity-20"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-900"></div>
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 h-full flex items-center justify-center">
                    <motion.div
                        initial="initial"
                        animate="animate"
                        variants={{
                            animate: { transition: { staggerChildren: 0.1 } },
                        }}
                        className="max-w-3xl text-center"
                    >
                        <motion.span
                            variants={fadeUp}
                            className="inline-block font-mono text-xs font-semibold tracking-widest uppercase text-blue-400 mb-6"
                        >
                            Core Competencies
                        </motion.span>

                        <motion.h1
                            variants={fadeUp}
                            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
                            data-testid="services-hero-title"
                        >
                            Our Solutions
                        </motion.h1>

                        <motion.p
                            variants={fadeUp}
                            className="text-lg text-slate-300 leading-relaxed"
                        >
                            Driving operational excellence and business growth.
                        </motion.p>
                    </motion.div>
                </div>
            </section>

            {/* Services Overview */}
            <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <span className="font-mono text-xs font-semibold tracking-widest uppercase text-blue-500 mb-4 block">
                            What We Offer
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                            Comprehensive ICT Solutions
                        </h2>
                        <p className="text-slate-600 max-w-2xl mx-auto">
                            From network infrastructure to managed services, we provide end-to-end
                            solutions that integrate people, processes, and technology.
                        </p>
                    </motion.div>

                    {/* Services Bento Grid */}
                    <div data-testid="services-bento-grid" className="space-y-8">
                        {coreCompetencies.map((service, index) => (
                            <motion.div
                                key={service.id}
                                id={service.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:border-blue-500/20 transition-all duration-300 overflow-hidden"
                            >
                                <div className="grid lg:grid-cols-3 gap-0">
                                    {/* Left Section */}
                                    <div className="p-8 md:p-10 lg:border-r border-slate-100">
                                        <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-6">
                                            <service.icon className="text-white" size={28} />
                                        </div>
                                        <h3 className="text-2xl font-bold text-slate-900 mb-3">
                                            {service.title}
                                        </h3>
                                        <p className="text-slate-600 leading-relaxed">
                                            {service.description}
                                        </p>
                                    </div>

                                    {/* Right Section - Features */}
                                    <div className="lg:col-span-2 p-8 md:p-10 bg-slate-50/50">
                                        <h4 className="font-mono text-xs font-semibold tracking-widest uppercase text-blue-500 mb-6">
                                            Key Capabilities
                                        </h4>
                                        <div className="grid sm:grid-cols-2 gap-4">
                                            {service.features.map((feature, fIndex) => (
                                                <div
                                                    key={fIndex}
                                                    className="flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-100"
                                                >
                                                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                                                        <feature.icon className="text-blue-500" size={18} />
                                                    </div>
                                                    <span className="text-slate-700 text-sm leading-relaxed pt-2">
                                                        {feature.text}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 md:py-32 bg-slate-900 px-6 md:px-12 lg:px-24">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <span className="font-mono text-xs font-semibold tracking-widest uppercase text-blue-400 mb-6 block">
                            Need a Custom Solution?
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                            Laying The Foundations For Your Success
                        </h2>
                        <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">
                            Every organization is unique.<br />
                            Let's build an infrastructure that scales with you.
                        </p>
                        <Link
                            to="/contact"
                            data-testid="services-cta"
                            className="inline-flex items-center gap-2 px-8 py-4 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 transition-colors shadow-[0_0_30px_rgba(59,130,246,0.4)]"
                        >
                            Request a Consultation
                            <ArrowRight size={18} />
                        </Link>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default ServicesPage;
